import { useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

import { useLoginMutation } from '@/app/services/authApi';
import { useNotification } from '@/hooks/useNotification';
import { InputTextField } from '@/components/ui/InputStandard';
import { PasswordField } from '@/components/ui';
import { getErrorMessage } from '@/utils/errorUtils';
import { AuthHeader, AuthFieldLabel, AuthPrimaryButton, AuthBottomNote } from './AuthPrimitives';
import { authInputSx } from './authStyles';

export interface LoginFormData {
  email: string;
  password: string;
}

export function LoginForm() {
  const [loginMutation, { isLoading }] = useLoginMutation();
  const { showErrorNotification } = useNotification();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    try {
      await loginMutation(data).unwrap();
    } catch (err) {
      const errorMessage =
        getErrorMessage(err as FetchBaseQueryError | SerializedError) ||
        'Incorrect email or password';
      showErrorNotification(errorMessage);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} className="auth-login-form">
      <AuthHeader title="Sign In" subtitle="Enter your credentials to access the platform" />

      <Box sx={{ mb: 4 }}>
        <AuthFieldLabel>Email</AuthFieldLabel>
        <InputTextField
          className="login-email-input"
          sx={authInputSx}
          id="email"
          placeholder="operator@osirisai.com"
          autoComplete="email"
          autoFocus
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' },
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
        />
      </Box>

      <Box sx={{ mb: 6 }}>
        <AuthFieldLabel>Password</AuthFieldLabel>
        <PasswordField
          className="login-password-input"
          sx={authInputSx}
          id="password"
          placeholder="Enter password"
          autoComplete="current-password"
          {...register('password', {
            required: 'Password is required',
            minLength: { value: 6, message: 'Password must be at least 6 characters' },
          })}
          error={!!errors.password}
          helperText={errors.password?.message}
        />
      </Box>

      <AuthPrimaryButton isLoading={isLoading} className="login-submit">
        Sign In
      </AuthPrimaryButton>

      <AuthBottomNote>
        Access issues? Contact your <a href="mailto:admin@osirisai.com">system administrator</a>
      </AuthBottomNote>
    </Box>
  );
}
